/**
 * The post-call survey. Required: did you call, anxiety before, anxiety after.
 * Everything else is one tap, and the notes box is folded away — a survey that
 * takes a minute costs reps (PRD §22).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiRequestError } from '../lib/api';
import { Button, Card, ErrorNote, Label, Loading, Screen, inputClass } from '../components/ui';
import {
  expectationResults,
  hesitationBuckets,
  type CallAssignment,
  type ExpectationResult,
  type HesitationBucket,
} from '../../shared/types';

function TapRow<T extends string>({
  label,
  options,
  value,
  onChange,
  labels,
}: {
  label: string;
  options: readonly T[];
  value: T | null;
  onChange: (next: T | null) => void;
  labels?: Record<T, string>;
}) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend>
        <Label>{label}</Label>
      </legend>
      <div className="flex gap-1.5">
        {options.map((option) => (
          <Button
            key={option}
            type="button"
            variant={value === option ? 'primary' : 'secondary'}
            className="flex-1 px-1 text-xs"
            aria-pressed={value === option}
            onClick={() => {
              onChange(value === option ? null : option);
            }}
          >
            {labels?.[option] ?? option}
          </Button>
        ))}
      </div>
    </fieldset>
  );
}

function AnxietySlider({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <Label>{label}</Label>
        <span className="text-2xl font-semibold text-mist-200">{value}</span>
      </div>
      <input
        type="range"
        min={1}
        max={10}
        value={value}
        aria-label={`${label}, 1 to 10`}
        onChange={(event) => {
          onChange(Number(event.target.value));
        }}
      />
    </div>
  );
}

export function CallResult() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const [assignment, setAssignment] = useState<CallAssignment | null>(null);
  const [madeCall, setMadeCall] = useState(true);
  const [before, setBefore] = useState(5);
  const [after, setAfter] = useState(3);
  const [hesitation, setHesitation] = useState<HesitationBucket | null>(null);
  const [expectation, setExpectation] = useState<ExpectationResult | null>(null);
  const [stretch, setStretch] = useState<boolean | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const alive = { current: true };
    void (async () => {
      try {
        const today = await api.today();
        const all = [...(today.next ? [today.next] : []), ...today.remaining, ...today.done];
        if (alive.current) setAssignment(all.find((item) => item.id === id) ?? null);
      } catch {
        if (alive.current) setAssignment(null);
      }
    })();
    return () => {
      alive.current = false;
    };
  }, [id]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const response = await api.submitResult(id, {
        madeCall,
        anxietyBefore: before,
        anxietyAfter: after,
        hesitationBucket: hesitation,
        expectationResult: expectation,
        stretchCompleted: stretch,
        notes: notes.trim() || null,
      });
      void navigate(response.dayComplete ? '/review' : '/today', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save that.');
      setBusy(false);
    }
  }

  return (
    <Screen title="How did it go?">
      {assignment ? (
        <p className="-mt-2 text-sm text-mist-400">
          <span className="text-mist-200">{assignment.businessName}</span> — {assignment.mission}
        </p>
      ) : (
        <Loading label="Finding the rep" />
      )}

      <Card className="flex flex-col gap-5">
        <fieldset className="flex flex-col gap-2">
          <legend>
            <Label>Did you make the call?</Label>
          </legend>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={madeCall ? 'primary' : 'secondary'}
              className="flex-1"
              aria-pressed={madeCall}
              onClick={() => {
                setMadeCall(true);
              }}
            >
              Yes
            </Button>
            <Button
              type="button"
              variant={!madeCall ? 'primary' : 'secondary'}
              className="flex-1"
              aria-pressed={!madeCall}
              onClick={() => {
                setMadeCall(false);
              }}
            >
              No
            </Button>
          </div>
        </fieldset>

        <AnxietySlider label="Anxiety before" value={before} onChange={setBefore} />
        <AnxietySlider label="Anxiety after" value={after} onChange={setAfter} />
      </Card>

      <Card className="flex flex-col gap-5">
        <TapRow
          label="Hesitation before dialling"
          options={hesitationBuckets}
          value={hesitation}
          onChange={setHesitation}
        />
        <TapRow
          label="How did it go?"
          options={expectationResults}
          value={expectation}
          onChange={setExpectation}
          labels={{ easier: 'Easier', expected: 'As expected', harder: 'Harder' }}
        />
        {assignment?.stretchGoal ? (
          <TapRow
            label="Stretch goal"
            options={['yes', 'no', 'n/a'] as const}
            value={stretch === null ? 'n/a' : stretch ? 'yes' : 'no'}
            onChange={(next) => {
              setStretch(next === 'yes' ? true : next === 'no' ? false : null);
            }}
            labels={{ yes: 'Done', no: 'Not this time', 'n/a': 'N/A' }}
          />
        ) : null}
      </Card>

      <details className="rounded-2xl border border-ink-700 bg-ink-900/60">
        <summary className="cursor-pointer px-4 py-3 text-sm text-mist-400">
          Anything worth remembering?
        </summary>
        <div className="px-4 pt-1 pb-4">
          <textarea
            className={`${inputClass} min-h-[6rem] resize-y`}
            value={notes}
            onChange={(event) => {
              setNotes(event.target.value);
            }}
            placeholder="Optional."
          />
        </div>
      </details>

      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <Button variant="primary" full onClick={() => void submit()} disabled={busy}>
        {busy ? 'Saving…' : 'Save rep'}
      </Button>
    </Screen>
  );
}
